// AuthContext.js - Proper version with role-based access control and timeout handling
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { logAuditEvent } from '../services/auditService';
import { unsubscribeFromPush } from '../utils/pushNotifications';

const AuthContext = createContext(null);
const PROFILE_CACHE_KEY = 'sb-profile-cache';

// Defer a task to the next macrotask. Used to keep awaited work OUT of
// supabase-js auth event callbacks, which the SDK invokes while still
// holding an internal auth lock (see AuthProvider below).
const defer = (fn) => setTimeout(fn, 0);

// ── Role Default Permissions ──────────────────────────────────────────
// Each role gets a default set of module permissions.
// Admins can override per-user in UserManagement.

export const ROLE_DEFAULTS = {
  employee: {
    mypayslip: { view: true },
    advances: { view: true, apply: true, approve: false, delete: false },
    leave: { view: true, apply: true, approve: false, delete: false },
    expense_claims: { view: true, apply: true, approve: false, delete: false },
    overtime: { view: true, apply: true, approve: false, delete: false },
    // Petty cash: employees can record their own receipts, but NOT issue advances/floats to themselves
    petty_cash: { view: true, apply: true, approve: false, issue_advance: false, delete: false },
    allowances_bonus: { view: false, manage: false },
    appraisals: { view: true },
    org_chart: { view: true },
    public_holidays: { view: true, manage: false },
    // Punch-card attendance: employees punch and view their own records
    attendance: { view: true, apply: true },
    apply_on_behalf: false,
  },
  manager: {
    mypayslip: { view: true },
    // Managers approve LEAVE and OT only, and only for their direct reports
    // (enforced via employees.reports_to). No advances/claims/petty-cash approval.
    advances: { view: true, apply: true, approve: false, delete: false },
    leave: { view: true, apply: true, approve: true, delete: false },
    expense_claims: { view: true, apply: true, approve: false, delete: false },
    overtime: { view: true, apply: true, approve: true, delete: false },
    // Petty cash: managers record their own receipts; they CANNOT approve or issue advances
    petty_cash: { view: true, apply: true, approve: false, issue_advance: false, delete: false },
    allowances_bonus: { view: false, manage: false },
    // Team-scoped employee visibility (direct reports + self), no crud
    employees: { view: true, crud: false },
    org_chart: { view: true },
    assets: { view: true },
    appraisals: { view: true },
    training: { view: true },
    public_holidays: { view: true, manage: false },
    // Managers punch their own + view their team's attendance
    attendance: { view: true, apply: true },
    // Managers record their OWN receipts/advances only — no on-behalf
    apply_on_behalf: false,
  },
  finance: {
    mypayslip: { view: true },
    // Finance approves money modules ONLY: advances, expense claims, petty cash.
    // Leave/OT approvals belong to managers/admins; finance is excluded from
    // Leave, OT, Appraisals, Assets and Training entirely.
    advances: { view: true, apply: true, approve: true, delete: false },
    expense_claims: { view: true, apply: true, approve: true, delete: false },
    petty_cash: { view: true, apply: true, approve: true, issue_advance: true, delete: false },
    company_expenses: { view: true },
    main_float: { view: true },
    unallocated_receipts: { view: true, allocate: true },
    allowances_bonus: { view: true, manage: true },
    org_chart: { view: true },
    public_holidays: { view: true, manage: false },
    payroll: { view: true, export: true },
    reports: { view: true, export: true },
    // Finance runs card-punch imports and manual punch entries
    attendance: { view: true, manage: true },
    // Segregation of duties: finance approves money modules, so it may NOT
    // apply on behalf (petty-cash custodian recording is a separate flow)
    apply_on_behalf: false,
  },
  admin: {
    mypayslip: { view: true },
    advances: { view: true, apply: true, approve: true, delete: true },
    leave: { view: true, apply: true, approve: true, delete: true },
    expense_claims: { view: true, apply: true, approve: true, delete: true },
    overtime: { view: true, apply: true, approve: true, delete: true },
    petty_cash: { view: true, apply: true, approve: true, issue_advance: true, delete: true },
    company_expenses: { view: true },
    main_float: { view: true },
    unallocated_receipts: { view: true, allocate: true },
    allowances_bonus: { view: true, manage: true },
    employees: { view: true, crud: true },
    org_chart: { view: true },
    onboarding: { view: true },
    assets: { view: true },
    appraisals: { view: true },
    training: { view: true },
    public_holidays: { view: true, manage: true },
    payroll: { view: true, run: true, export: true },
    reports: { view: true, export: true },
    document_alerts: { view: true, manage: true },
    settings: { users: true, company: true, audit_logs: true },
    attendance: { view: true, manage: true },
    apply_on_behalf: true,
  },
  super_admin: {
    // Super admin does NOT see mypayslip — they manage the system, not personal payslips
    advances: { view: true, apply: true, approve: true, delete: true },
    leave: { view: true, apply: true, approve: true, delete: true },
    expense_claims: { view: true, apply: true, approve: true, delete: true },
    overtime: { view: true, apply: true, approve: true, delete: true },
    petty_cash: { view: true, apply: true, approve: true, issue_advance: true, delete: true },
    company_expenses: { view: true },
    main_float: { view: true },
    unallocated_receipts: { view: true, allocate: true },
    allowances_bonus: { view: true, manage: true },
    employees: { view: true, crud: true },
    org_chart: { view: true },
    onboarding: { view: true },
    assets: { view: true },
    appraisals: { view: true },
    training: { view: true },
    public_holidays: { view: true, manage: true },
    payroll: { view: true, run: true, export: true },
    reports: { view: true, export: true },
    document_alerts: { view: true, manage: true },
    settings: { users: true, company: true, system: true },
    attendance: { view: true, manage: true },
    apply_on_behalf: true,
  },
};

// Merge role defaults with user-specific permission overrides
function mergePermissions(defaults, overrides) {
  const result = { ...defaults };
  for (const key of Object.keys(overrides || {})) {
    if (
      typeof overrides[key] === 'object' &&
      overrides[key] !== null &&
      !Array.isArray(overrides[key])
    ) {
      result[key] = { ...defaults[key], ...overrides[key] };
    } else {
      result[key] = overrides[key];
    }
  }
  return result;
}

export function getEffectivePermissions(userProfile) {
  const defaults = ROLE_DEFAULTS[userProfile?.role] || {};
  const overrides = userProfile?.permissions || {};
  return mergePermissions(defaults, overrides);
}


export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);
  const [error, setError] = useState(null);
  const profileLoadedRef = useRef(false); // Track if profile has been loaded

  // Fetch user profile from database with timeout
  const fetchUserProfile = async (userId, userEmail) => {
    try {
      console.log('[Auth] Fetching user profile for:', userId);

      // Select only fields needed by AuthContext and consumers.
      // Avoids SELECT * overhead and reduces payload on slow connections.
      const profileQuery = supabase
        .from('user_profiles')
        .select('id, role, employee_id, company_ids, managed_company_ids, default_company_id, display_name, is_active, permissions, avatar_url, formal_photo_url, email_notifications, push_subscription, created_at, updated_at')
        .eq('id', userId)
        .single();

      // Fetch with timeout using AbortController
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      const { data, error } = await profileQuery.abortSignal(controller.signal);
      clearTimeout(timeoutId);

      if (error) {
        console.error('[Auth] Error fetching profile:', error);
        // Profile not found — user exists in auth but has no user_profiles record
        // Try to auto-link by email before giving up
        if (error.code === 'PGRST116' || !data) {
          console.warn('[Auth] Profile not found or fetch failed. Attempting auto-link by email...');
          try {
            const empEmail = userEmail || user?.email;
            if (empEmail) {
              const { data: empRow } = await supabase
                .from('employees')
                .select('id, full_name, company_id')
                .eq('email', empEmail)
                .eq('status', 'active')
                .maybeSingle();
              if (empRow) {
                console.log('[Auth] Auto-linked employee from fallback:', empRow.id, empRow.full_name);
                // Create a working profile with the linked employee_id
                const linkedProfile = {
                  id: userId,
                  role: 'employee',
                  employee_id: empRow.id,
                  company_ids: empRow.company_id ? [empRow.company_id] : [],
                  default_company_id: empRow.company_id || null,
                  is_active: true,
                  display_name: empRow.full_name || empEmail?.split('@')[0] || 'User',
                  email: empEmail || ''
                };
                // Persist the link in user_profiles for future loads
                supabase
                  .from('user_profiles')
                  .upsert({ id: userId, role: 'employee', employee_id: empRow.id, company_ids: empRow.company_id ? [empRow.company_id] : [], display_name: empRow.full_name })
                  .then(({ error: insErr }) => {
                    if (insErr) console.warn('[Auth] Failed to create user_profiles row:', insErr);
                    else console.log('[Auth] user_profiles row created with linked employee_id');
                  });
                return linkedProfile;
              }
            }
          } catch (linkErr) {
            console.warn('[Auth] Auto-link from fallback failed:', linkErr);
          }
        }
        // If auto-link failed, return minimal fallback
        return {
          id: userId,
          role: 'employee',
          employee_id: null,
          company_ids: [],
          is_active: true,
          display_name: (userEmail || user?.email)?.split('@')[0] || 'User',
          email: userEmail || user?.email || ''
        };
      }

      console.log('[Auth] Profile loaded:', data);
      console.log('[Auth] Profile employee_id:', data?.employee_id, 'role:', data?.role);

      // Auto-link employee_id by email when missing for employee/manager/finance roles.
      // This handles cases where user_profiles row exists but employee_id is not linked,
      // causing forms to fail with "Please select an employee".
      if (!data?.employee_id && data?.role && ['employee', 'manager', 'finance'].includes(data.role)) {
        console.warn('[Auth] employee_id is null for role', data.role, '- attempting auto-link by email');
        try {
          const empEmail = userEmail || user?.email;
          if (empEmail) {
            const { data: empRow } = await supabase
              .from('employees')
              .select('id, full_name, company_id')
              .eq('email', empEmail)
              .eq('status', 'active')
              .maybeSingle();
            if (empRow) {
              console.log('[Auth] Auto-linked employee:', empRow.id, empRow.full_name);
              data.employee_id = empRow.id;
              data.company_ids = [empRow.company_id];
              // Persist the link so future loads don't need auto-linking
              supabase
                .from('user_profiles')
                .update({ employee_id: empRow.id, company_ids: [empRow.company_id] })
                .eq('id', userId)
                .then(({ error: updErr }) => {
                  if (updErr) console.warn('[Auth] Failed to auto-update user_profiles:', updErr);
                  else console.log('[Auth] user_profiles.employee_id updated permanently');
                });
            } else {
              console.warn('[Auth] No active employee found with email:', empEmail);
            }
          }
        } catch (linkErr) {
          console.warn('[Auth] Auto-link failed:', linkErr);
        }
      }

      // Cache profile for instant load on next refresh
      // Only cache if employee_id is set — don't cache incomplete profiles
      // that would show "not linked" on the next load
      if (data?.employee_id || data?.role === 'admin' || data?.role === 'super_admin' || data?.role === 'finance') {
        try {
          localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify({ userId, profile: data, ts: Date.now() }));
        } catch (e) { /* localStorage full or private mode */ }
      }
      return data;
    } catch (err) {
      console.error('[Auth] Failed to fetch user profile:', err);
      // Return null instead of throwing - allow app to continue
      return null;
    }
  };

  // Initialize auth state.
  //
  // CRITICAL (refresh deadlock fix): supabase-js invokes onAuthStateChange
  // callbacks while it still holds an INTERNAL auth lock. Awaiting any
  // supabase query inside the callback deadlocks — every query needs that
  // same lock (via getSession) to build its Authorization header, and the
  // lock is only released after the callback returns. That deadlock is what
  // made every page refresh hang on the spinner forever, while login worked
  // (SIGNED_IN is emitted outside the locked init path).
  //
  // Rule: the listener performs ONLY synchronous setState calls; all awaited
  // work (profile fetch) is deferred to a macrotask via defer().
  useEffect(() => {
    let isMounted = true;

    // Last-resort failsafe: if neither the listener nor getSession produces a
    // verdict (dead network, wedged SDK state), unblock the UI after 12s.
    // ProtectedRoute then shows its recovery actions — never an infinite spinner.
    const failsafe = setTimeout(() => {
      if (isMounted) setLoading(false);
    }, 12000);

    // Load cached profile from localStorage (synchronous, <1ms).
    // Pre-populates the profile so the UI paints instantly while auth resolves.
    const loadCachedProfile = () => {
      try {
        const raw = localStorage.getItem(PROFILE_CACHE_KEY);
        if (raw) {
          const { profile, ts } = JSON.parse(raw);
          if (profile && Date.now() - ts < 24 * 60 * 60 * 1000) {
            return profile;
          }
        }
      } catch (e) { /* corrupted cache */ }
      return null;
    };

    const cached = loadCachedProfile();
    if (cached) {
      setUserProfile(cached);
    }

    // Syncs the signed-in user into state and kicks off a background profile
    // refresh. Safe to call from inside auth callbacks: the only awaited work
    // runs in a deferred macrotask, after the SDK has released its lock.
    const runSessionSync = (sessionUser) => {
      if (!sessionUser) {
        // Definitive "not signed in": INITIAL_SESSION with no session.
        // Never clobbers an already-synced user (ordering guard below).
        if (!profileLoadedRef.current) {
          setUser(null);
          setUserProfile(null);
          setLoading(false);
        }
        return;
      }
      setUser(sessionUser);
      if (profileLoadedRef.current) return; // already syncing this session
      profileLoadedRef.current = true;
      const userId = sessionUser.id;
      const userEmail = sessionUser.email;
      defer(async () => {
        // Note: no isMounted check here — under StrictMode's dev double-mount
        // the second mount relies on this first-mount fetch completing
        // (profileLoadedRef is shared across both mounts). Set-state after a
        // genuine unmount is a harmless no-op in React 18+.
        try {
          const profile = await fetchUserProfile(userId, userEmail);
          if (!profile) return; // keep cached profile on failure
          setUserProfile(profile);
        } catch (err) {
          console.warn('[Auth] Profile sync failed (non-blocking):', err?.message);
        }
      });
    };

    // Set up auth state listener — fires INITIAL_SESSION / SIGNED_IN / etc.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!isMounted) return;

        if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
          if (event === 'SIGNED_IN') {
            localStorage.removeItem('selectedCompanyId');
          }
          // Unblock the UI immediately — the cached profile paints instantly
          // and the fresh profile arrives via the deferred sync below.
          setLoading(false);
          if (session?.user) {
            runSessionSync(session.user);
          } else if (event === 'INITIAL_SESSION') {
            runSessionSync(null);
          }
        } else if (event === 'TOKEN_REFRESHED') {
          if (session?.user) setUser(session.user);
        } else if (event === 'SIGNED_OUT') {
          localStorage.removeItem('selectedCompanyId');
          setUser(null);
          setUserProfile(null);
          profileLoadedRef.current = false;
          setLoading(false);
        }
      }
    );

    // Validate the session directly, in parallel with the listener. The
    // profileLoadedRef guard makes this a no-op when the listener got there
    // first. Hard 8s cap: getSession can wait on the SDK's internal lock, and
    // a hung validation must never hold the UI hostage.
    const validateSession = async () => {
      try {
        const result = await Promise.race([
          supabase.auth.getSession(),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('getSession timed out')), 8000)
          ),
        ]);
        if (!isMounted) return;
        const session = result?.data?.session;
        if (session?.user) {
          runSessionSync(session.user);
        }
        // No session: the listener's null INITIAL_SESSION path (which always
        // fires) is responsible for setLoading(false).
      } catch (err) {
        console.warn('[Auth] Session validation issue (listener remains authoritative):', err?.message);
      }
    };

    validateSession();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      clearTimeout(failsafe);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Watch for force_password_change flag in auth metadata
  useEffect(() => {
    const forced = !!user?.user_metadata?.force_password_change;
    setRequiresPasswordChange(forced);
  }, [user?.user_metadata?.force_password_change]);

  // Login with timeout
  const login = async (email, password) => {
    setError(null);
    try {
      console.log('[Auth] Attempting login for:', email);

      const loginPromise = supabase.auth.signInWithPassword({
        email,
        password,
      });

      const { data, error } = await loginPromise;

      if (error) throw error;

      console.log('[Auth] Login successful');
      await logAuditEvent({ action: 'LOGIN', tableName: 'auth.users', recordId: data.user?.id || email });
      return { success: true, user: data.user };
    } catch (err) {
      console.error('[Auth] Login error:', err);
      const errorMsg = err.message || 'Login timeout - please check your connection';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  // Logout with timeout
  const logout = async () => {
    try {
      console.log('[Auth] Logging out...');

      // Log audit event BEFORE signing out, while auth session is still valid
      const currentUserId = user?.id || 'unknown';
      await logAuditEvent({ action: 'LOGOUT', tableName: 'auth.users', recordId: currentUserId });

      // Sign out (with timeout to prevent hanging)
      try {
        await Promise.race([
          supabase.auth.signOut(),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Signout timeout')), 3000)),
        ]);
      } catch { /* timeout or error — continue cleanup regardless */ }

      // Unsubscribe from push notifications on logout
      unsubscribeFromPush(user?.id).catch(() => {});
      console.log('[Auth] Logout successful');
      setUser(null);
      setUserProfile(null);
      profileLoadedRef.current = false; // Reset profile loaded flag
      localStorage.clear();
      return { success: true };
    } catch (err) {
      console.error('[Auth] Logout error (will force logout):', err);
      // Force logout even on error/timeout
      setUser(null);
      setUserProfile(null);
      profileLoadedRef.current = false; // Reset profile loaded flag
      localStorage.clear();
      return { success: true };
    }
  };

  // Role checks
  const hasRole = (roles) => {
    if (!userProfile) return false;
    if (typeof roles === 'string') {
      return userProfile.role === roles;
    }
    return roles.includes(userProfile.role);
  };

  const isSuperAdmin = () => {
    return userProfile?.role === 'super_admin';
  };

  const isAdmin = () => {
    return userProfile?.role === 'admin' || userProfile?.role === 'super_admin';
  };

  const isManagerOrAbove = () => {
    return userProfile?.role === 'manager' || userProfile?.role === 'finance' || userProfile?.role === 'admin' || userProfile?.role === 'super_admin';
  };

  // ── Permission System ───────────────────────────────────────────────
  const permissions = getEffectivePermissions(userProfile);

  const hasPermission = (module, action) => {
    return !!permissions[module]?.[action];
  };

  const canAccessModule = (module) => {
    const modPerms = permissions[module];
    if (!modPerms) return false;
    if (typeof modPerms === 'boolean') return modPerms;
    return Object.values(modPerms).some(v => v === true);
  };

  const getUserPermissions = () => permissions;

  const getDisplayName = () => {
    if (userProfile?.display_name) return userProfile.display_name;
    if (user?.email) return user.email.split('@')[0];
    return 'User';
  };

  const updateEmail = async (newEmail) => {
    try {
      const { error } = await supabase.auth.updateUser({ email: newEmail });
      if (error) throw error;
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const updatePassword = async (newPassword) => {
    try {
      // Update password AND clear force_password_change flag in one call
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
        data: { force_password_change: false }
      });
      if (error) throw error;

      // Refresh user object so metadata changes are reflected immediately
      const { data: { user: refreshedUser }, error: refreshErr } = await supabase.auth.getUser();
      if (!refreshErr && refreshedUser) {
        setUser(refreshedUser);
        setRequiresPasswordChange(!!refreshedUser.user_metadata?.force_password_change);
      }

      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const resetPassword = async (email) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/setup-profile`,
      });
      if (error) throw error;
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const refreshProfile = async () => {
    if (user) {
      console.log('[Auth] Manually refreshing profile');
      const profile = await fetchUserProfile(user.id, user?.email);
      if (profile) {
        setUserProfile(profile);
        profileLoadedRef.current = true;
      }
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    error,
    requiresPasswordChange,
    login,
    logout,
    hasRole,
    isSuperAdmin,
    isAdmin,
    isManagerOrAbove,
    updateEmail,
    updatePassword,
    resetPassword,
    getDisplayName,
    refreshProfile,
    isAuthenticated: !!user,
    permissions,
    hasPermission,
    canAccessModule,
    getUserPermissions,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
